/**
 * PrecipAnimGLLayer.ts
 * أنيميشن الهطول (مطر متساقط وثلج متطاير) فوق طبقة الأمطار — **منقول عن شيدر Zoom Earth
 * نفسه** لا عن تقدير بصري.
 *
 * لماذا شيدر لا جسيمات DOM/Canvas:
 * فُكّت حزمة Zoom Earth (`https://zoom.earth/assets/js/app.3aebf9c1.js`) فتبيّن أن هطولها
 * ليس جسيمات إطلاقاً: هو **دالّتان إجرائيّتان داخل شيدر الجزء (fragment shader) نفسه الذي
 * يلوّن حقل الأمطار**. لذلك:
 *
 *   • لا يمكن أن يخرج قطرة واحدة خارج منطقة الهطول: البكسل الذي يقرّر اللون هو البكسل
 *     الذي يقرّر القطرة. لا "شبكة عيّنات" ولا "قناع" ولا هامش خطأ نصف خليّة — الشرط
 *     يُفحص لكل بكسل شاشة على حدة.
 *   • لا يتحرّك شيء عند تحريك الخريطة: إحداثي النمط هو إحداثي مركاتور العالمي مضروباً في
 *     حجم العالم بالبكسل، أي مسمَّر على الأرض بحكم تعريفه.
 *   • الكثافة ثابتة عند أي تكبير بلا أي منطق LOD.
 *   • عدد "القطرات" غير محدود عملياً (لكل بكسل قيمته) — لا "أربع أو خمس قطرات".
 *
 * أربع محاولات سابقة بجسيمات DOM ثم Canvas فشلت في واحدة أو أكثر من هذه النقاط، وكلّها
 * فشلت لأنها تقارب مسألةً هي أصلاً لكل-بكسل بشبكة عيّنات متفرّقة.
 *
 * الدوالّ أدناه (`raindrops` و`N11` و`N21` و`snowLayer` و`blizzard`) منقولة حرفياً من
 * حزمة Zoom Earth مع إعادة التنسيق والتسمية فقط — بما فيها الثوابت (871.213، 3134.422،
 * 4e-3، 0.03، 12.0، 0.04/0.02، 600.0، 11 طبقة، mix(1,.5,i)). تنوّع أحجام حبّات الثلج
 * يأتي من هناك: ١١ طبقة ثلج بمقاييس متدرّجة 1.0→0.5، فالطبقة الأصغر مقياساً تُنتج حبّات
 * أكبر على الشاشة — حبّة صغيرة وحبّة أكبر في المشهد نفسه.
 *
 * الفروق المقصودة عن الأصل:
 *   ❶ مصدر البيانات: عندنا راستر هطول رمادي واحد (تراكم ٣ ساعات) + راستر حرارة نفصل به
 *      المطر عن الثلج، بدل قناتَي rain/snow المنفصلتين عندهم.
 *   ❷ عتبة الظهور: طلب صريح ألّا يظهر شيء عند 0 مم ولا في المدى 0.0–0.5 مم. عتبة Zoom
 *      Earth (g>.05 على قناة مضغوطة أُسّياً) تعني عملياً "أي هطول مهما ضؤل".
 *   ❸ تخفيف بياض المطر فوق خلايا البرق (راجع u_storm أدناه) كي تبقى الصواعق مقروءة.
 *
 * ملاحظة على ضغط القيمة: قناة الهطول عند Zoom Earth مخزَّنة مرفوعةً للأس ⅛
 * (`n = min + t.g**8 * span` عند الفكّ)، ولذلك تُضرَب في 0.3 فقط. راسترنا خطّي، فنطبّق
 * نفس الضغط هنا (`pow(mm/max, 1/8)`) وإلّا خرج المطر شبه معدوم عند القيم الواقعية.
 */

import { MercatorCoordinate, type CustomLayerInterface, type CustomRenderMethodInput, type Map as MaplibreMap } from 'maplibre-gl';
import { type GLContext, createProgram, getLocations, createTexture, bindTexture } from './glUtils';
import { loadRasterImage } from '../../../services/rasterImageCache';
import { VALUE_RANGES } from './weatherTextures';

const LAT_LIMIT = 85.0511287798066;

const VERTEX_SRC = `
attribute vec2 a_pos;
uniform mat4 u_matrix;
varying vec2 v_merc;
void main() { v_merc = a_pos; gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0); }`;

const FRAGMENT_SRC = `
precision highp float;
varying vec2 v_merc;
uniform sampler2D u_precip;
uniform sampler2D u_temp;
uniform sampler2D u_storm;
uniform float u_hasTemp;
uniform float u_hasStorm;
uniform float u_time;
uniform float u_worldPx;
uniform float u_opacity;
uniform float u_minMm;

const float PI = 3.141592653589793;
const float PRECIP_MAX = ${VALUE_RANGES.precipitation[1].toFixed(1)};
const float TEMP_MIN = ${VALUE_RANGES.temperature[0].toFixed(1)};
const float TEMP_SPAN = ${(VALUE_RANGES.temperature[1] - VALUE_RANGES.temperature[0]).toFixed(1)};
const float STORM_MAX = ${VALUE_RANGES['k-index'][1].toFixed(1)};
/** حرارة السطح التي تحتها يُصنَّف الهطول ثلجاً. */
const float SNOW_C = 1.0;

// ── منقول حرفياً من شيدر Zoom Earth ───────────────────────────────────────────
// خيوط مطر: منحنى منشاري رأسي؛ القيمة تنفجر عند بداية كل دورة (رأس الخيط الساطع) ثم
// تخبو حتى نهايتها (ذيله). دورة كل 250 وحدة عالمية (1/4e-3)، وسرعة كل عمود عرضه وحدتان
// تختلف قليلاً عن جاره (cos على x المقرَّبة لمضاعفات 2) — فلا تسقط الخيوط كلّها معاً.
float raindrops(vec2 uv, float t) {
    float v = cos(uv.x - mod(uv.x, 2.0)) * 0.15 + 0.85;
    return min(1.0, 0.03 / fract(uv.y * 4e-3 + t * v));
}

float N11(float n) { return fract(sin(n * 871.213) * 3134.422); }
float N21(vec2 uv) { return N11(N11(uv.x) + uv.y); }

// طبقة ثلج واحدة: شبكة خلايا، في كل خلية حبّة واحدة تتأرجح داخلها بجيبَين مختلفَي الطور
// (فتتمايل الحبّة يميناً/يساراً وهي تهبط بدل السقوط المستقيم).
float snowLayer(vec2 uv, float t) {
    uv.y += t * 0.5;
    vec2 p = fract(uv * 12.0), id = floor(uv * 12.0);
    p.x += sin(N21(id) * 128.0 + t) * 0.4;
    p.y += sin(N11(N21(id)) * 128.0 + t) * 0.4;
    return smoothstep(0.04, 0.02, distance(vec2(0.5), p));
}

// ١١ طبقة ثلج بمقاييس متدرّجة 1.0→0.5 وإزاحات عشوائية. **هنا يأتي تنوّع أحجام الحبّات**:
// الطبقة ذات المقياس الأصغر تُنتج خلايا أكبر ⇒ حبّات أكبر على الشاشة، فيجتمع في المشهد
// الواحد حبّ صغير وحبّ أكبر بسرعات ظاهرية مختلفة (إحساس العمق).
float blizzard(vec2 uv, float time) {
    float t = time * 0.2, s = 0.0;
    for (int k = 0; k < 11; k++) {
        float i = float(k) * 0.1;
        float z = mix(1.0, 0.5, i);
        vec2 o = vec2(N11(i), N11(N11(i)));
        s += snowLayer((uv / 600.0 + o) * z, t);
        if (s >= 1.0) break;
    }
    return s;
}
// ──────────────────────────────────────────────────────────────────────────────

void main() {
    // الطول يُلَفّ لنسخة عالم واحدة (fract) فتظهر الطبقة في كل النسخ المكرّرة.
    float u = fract(v_merc.x);
    float latRad = 2.0 * atan(exp(PI * (1.0 - 2.0 * v_merc.y))) - PI * 0.5;
    float lat = degrees(latRad);
    if (lat < -90.0 || lat > 90.0) discard;
    float v = (lat + 90.0) / 180.0;
    vec2 tuv = vec2(u, v);

    float mm = texture2D(u_precip, tuv).r * PRECIP_MAX;
    // الشرط الصارم: لا شيء عند 0 مم ولا داخل المدى 0.0–0.5 مم.
    if (mm <= u_minMm) discard;

    // مطر أم ثلج؟ بلا نسيج حرارة نفترض مطراً (لا نخترع ثلجاً بلا دليل).
    float tempC = u_hasTemp > 0.5 ? (TEMP_MIN + texture2D(u_temp, tuv).r * TEMP_SPAN) : 99.0;
    float isSnow = step(tempC, SNOW_C);

    // إحداثي النمط = إحداثي مركاتور × حجم العالم بالبكسل ⇒ مسمَّر على الأرض عند التحريك،
    // وثابت الكثافة على الشاشة عند التكبير. نستعمل الطول الملفوف كي تبقى الأعداد ضمن نسخة
    // عالم واحدة (دقّة float32 تنهار في fract/sin عند أعداد أكبر بكثير).
    //
    // **إشارة y معكوسة عمداً.** دالّتا Zoom Earth تُحرّكان النمط بزيادة الزمن في اتجاه
    // y **الموجب لأعلى**: في raindrops يقع رأس الخيط عند uv.y = (n - t*v)/4e-3، وفي
    // snowLayer تقع الحبّة عند uv.y = c - t*0.5 — وكلاهما uv.y متناقص مع الزمن.
    // ومحور مركاتور عندنا معاكس — y = 0 عند القطب الشمالي
    // ويزداد جنوباً، أي في اتجاه الأسفل على الشاشة. فبلا هذا القلب يتحرّك المطر والثلج
    // **صعوداً** بدل السقوط.
    vec2 uv = vec2(u, -v_merc.y) * u_worldPx;

    // نفس ضغط قناة Zoom Earth (أس ⅛) وإلّا صار المطر شبه معدوم عند القيم الواقعية.
    float g = pow(clamp(mm / PRECIP_MAX, 0.0, 1.0), 0.125);

    float rain = (1.0 - isSnow) * min(1.0, g * 0.3 * raindrops(uv, u_time));
    float snow = isSnow * min(1.0, g * 1.3 * blizzard(uv, u_time));

    // البرق فوق الخليّة العاصفية: خيوط المطر البيضاء كانت تطمس الصواعق. نخفّف بياض المطر
    // فوق النشاط الرعدي وحده (لا الثلج — لا برق مع الثلج عملياً) فتبقى الصاعقة مقروءة.
    // العتبتان مطابقتان لعتبة عرض الصاعقة في LightningLayer.tsx: يبدأ التخفيف عند K=22
    // (بداية "عواصف معزولة محتملة" وأدنى عتبة نرسم عندها صاعقة) ويبلغ أقصاه عند K=35
    // (بداية "عواصف واسعة الانتشار") — فلا نخفّف مطراً في خلية لا صاعقة فوقها أصلاً.
    float dim = 1.0;
    if (u_hasStorm > 0.5) {
        float storm = texture2D(u_storm, tuv).r * STORM_MAX;
        dim = 1.0 - 0.65 * smoothstep(22.0, 35.0, storm);
    }

    float a = clamp(rain * dim + snow, 0.0, 1.0) * u_opacity;
    if (a <= 0.001) discard;
    // ألفا مضروبة مسبقاً (blendFunc = ONE, ONE_MINUS_SRC_ALPHA) — يعطي بالضبط
    // mix(precipColor, vec3(0.95), a) وهو تركيب Zoom Earth نفسه.
    gl_FragColor = vec4(vec3(0.95) * a, a);
}`;

type Slot = 'precip' | 'temp' | 'storm';

export class PrecipAnimGLLayer implements CustomLayerInterface {
    id: string;
    type = 'custom' as const;
    renderingMode = '2d' as const;

    private map: MaplibreMap | null = null;
    private glRef: GLContext | null = null;
    private program: WebGLProgram | null = null;
    private loc: ReturnType<typeof getLocations> | null = null;
    private quadBuffer: WebGLBuffer | null = null;
    private destroyed = false;

    private tex: Record<Slot, WebGLTexture | null> = { precip: null, temp: null, storm: null };
    private ready: Record<Slot, boolean> = { precip: false, temp: false, storm: false };
    private url: Record<Slot, string> = { precip: '', temp: '', storm: '' };
    /** أجيال التحميل: صورة متأخّرة من إطار/طبقة سابقة يجب ألّا تكتب فوق الأحدث. */
    private gen: Record<Slot, number> = { precip: 0, temp: 0, storm: 0 };

    private startMs = performance.now();
    private opacity = 1;
    private minMm = 0.5;

    constructor(id: string, opacity = 1, minMm = 0.5) {
        this.id = id;
        this.opacity = opacity;
        this.minMm = minMm;
    }

    setUrl(slot: Slot, url: string) {
        if (url === this.url[slot]) return;
        this.url[slot] = url;
        if (!url) { this.ready[slot] = false; this.gen[slot]++; return; }
        if (this.glRef) this.load(this.glRef, slot);
    }

    setOpacity(o: number) { this.opacity = o; this.map?.triggerRepaint(); }

    onAdd(map: MaplibreMap, gl: GLContext) {
        this.map = map;
        this.glRef = gl;
        this.program = createProgram(gl, VERTEX_SRC, FRAGMENT_SRC);
        this.loc = getLocations(gl, this.program, ['a_pos'],
            ['u_matrix', 'u_precip', 'u_temp', 'u_storm', 'u_hasTemp', 'u_hasStorm',
             'u_time', 'u_worldPx', 'u_opacity', 'u_minMm']);

        const blank = new Uint8Array([0, 0, 0, 0]);
        this.tex.precip = createTexture(gl, 1, 1, blank, { filter: gl.LINEAR });
        this.tex.temp = createTexture(gl, 1, 1, blank, { filter: gl.LINEAR });
        this.tex.storm = createTexture(gl, 1, 1, blank, { filter: gl.LINEAR });

        // رباعي يغطّي عدّة نسخ عالم أفقياً — نفس منطق RasterHeatmapGLLayer كي تظهر الطبقة
        // في كل النسخ المكرّرة أثناء التحريك بلا فراغات.
        const yN = MercatorCoordinate.fromLngLat({ lng: 0, lat: LAT_LIMIT }).y;
        const yS = MercatorCoordinate.fromLngLat({ lng: 0, lat: -LAT_LIMIT }).y;
        const N = 3, x0 = -N, x1 = 1 + N;
        this.quadBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([x0, yN, x0, yS, x1, yN, x1, yS]), gl.STATIC_DRAW);

        (Object.keys(this.url) as Slot[]).forEach((s) => { if (this.url[s]) this.load(gl, s); });
    }

    onRemove() {
        this.destroyed = true;
        const gl = this.glRef;
        if (gl) {
            (Object.keys(this.tex) as Slot[]).forEach((s) => { if (this.tex[s]) gl.deleteTexture(this.tex[s]!); });
            if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer);
            if (this.program) gl.deleteProgram(this.program);
        }
        this.tex = { precip: null, temp: null, storm: null };
        this.map = null;
    }

    private load(gl: GLContext, slot: Slot) {
        const tex = this.tex[slot];
        const src = this.url[slot];
        if (!tex || !src) return;
        const gen = ++this.gen[slot];
        void loadRasterImage(src).then((img) => {
            // قد تصل الصورة بعد إزالة الطبقة أو بعد تجاوز طلبها (تبديل إطار أثناء التحميل).
            if (this.destroyed || gen !== this.gen[slot] || !this.tex[slot]) return;
            try {
                gl.bindTexture(gl.TEXTURE_2D, this.tex[slot]!);
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                /*
                 * ── لا `gl.getError()` هنا ────────────────────────────────────
                 *
                 * قياس (‏Chrome trace): **3.6 ثانية زمناً ذاتياً (‏4.6%)** في هذا النداء
                 * وحده. والسبب بنيوي لا عارض: `getError` نقطةُ مزامنة تُجبر وحدة
                 * المعالجة المركزية على الانتظار حتى تُفرِغ الـGPU خطّ أنابيبها —
                 * فبعد `texImage2D` مباشرةً تعني الانتظار حتى يكتمل رفع النسيج. قِيس
                 * ~40 مللي لكل نداء، مرّتين لكل خطوة ساعة.
                 *
                 * وما كان يحرسه لا يقع عملياً: مصدر الصورة `ImageBitmap` معروف الأبعاد
                 * وقد فُكّ بنجاح، و`try/catch` أعلاه يلتقط النسيج المحذوف تحت سباق
                 * الإزالة. أمّا تجاوز `MAX_TEXTURE_SIZE` فلا ينطبق على 1440×721.
                 *
                 * نفس المعالجة طُبّقت في RasterHeatmapGLLayer للسبب نفسه.
                 */
                this.ready[slot] = true;
                this.map?.triggerRepaint();
            } catch { /* نسيج محذوف تحت سباق إزالة — تُتجاهَل */ }
        }).catch(() => undefined);
    }

    render(gl: GLContext, options: CustomRenderMethodInput) {
        if (this.destroyed || !this.program || !this.loc || !this.quadBuffer) return;
        // بلا نسيج هطول لا شيء يُرسم — ولا نُبقي إطاراً قديماً معلّقاً.
        if (!this.ready.precip) return;

        const map = this.map;
        gl.useProgram(this.program);
        gl.uniformMatrix4fv(this.loc.uniforms.u_matrix, false,
            new Float32Array(options.defaultProjectionData.mainMatrix as ArrayLike<number>));
        gl.uniform1f(this.loc.uniforms.u_time, (performance.now() - this.startMs) / 1000);
        // حجم العالم بالبكسل عند التكبير الحالي (بلاطة MapLibre 512) — يحوّل إحداثي
        // مركاتور إلى بكسل عالمي، فيبقى النمط مسمَّراً على الأرض وثابت الكثافة على الشاشة.
        gl.uniform1f(this.loc.uniforms.u_worldPx, 512 * Math.pow(2, map?.getZoom() ?? 0));
        gl.uniform1f(this.loc.uniforms.u_opacity, this.opacity);
        gl.uniform1f(this.loc.uniforms.u_minMm, this.minMm);
        gl.uniform1f(this.loc.uniforms.u_hasTemp, this.ready.temp ? 1 : 0);
        gl.uniform1f(this.loc.uniforms.u_hasStorm, this.ready.storm ? 1 : 0);

        bindTexture(gl, this.tex.precip!, 0); gl.uniform1i(this.loc.uniforms.u_precip, 0);
        bindTexture(gl, this.tex.temp!, 1); gl.uniform1i(this.loc.uniforms.u_temp, 1);
        bindTexture(gl, this.tex.storm!, 2); gl.uniform1i(this.loc.uniforms.u_storm, 2);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
        gl.enableVertexAttribArray(this.loc.attributes.a_pos);
        gl.vertexAttribPointer(this.loc.attributes.a_pos, 2, gl.FLOAT, false, 0, 0);
        gl.disable(gl.CULL_FACE);
        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        // الحركة تحتاج إطاراً تالياً دائماً — الخريطة ساكنة ما لم نطلب إعادة الرسم.
        map?.triggerRepaint();
    }
}
